/**
 * P3.3 — Cookie consent context.
 *
 * Captures, persists, and exposes the user's per-category cookie consent.
 * Persisted to localStorage under `cm_cookie_consent_v1` (v1 in the key so
 * a future categorization change can re-prompt without confusing existing
 * consents).
 *
 * Categories:
 *   essential  — always on, no toggle. Login token, language, consent
 *                state itself.
 *   analytics  — Sentry (when wired), PostHog (P2.11), aggregate funnel
 *                tracking. Gates the `useAnalyticsConsent()` hook so
 *                callers can short-circuit.
 *   marketing  — retargeting pixels, email open tracking, marketing
 *                attribution. Not used by CoffeeMates today; reserved.
 *   preferences — UI tweaks remembered across visits (theme, last-viewed
 *                 venue, etc.).
 *
 * Default state on first visit: NO non-essential consent. Banner shown.
 * After Save/AcceptAll/RejectAll, banner hides; choice persists.
 */

import { createContext, useContext, useState, useEffect, useCallback, useMemo, ReactNode } from 'react';

export type CookieCategory = 'essential' | 'analytics' | 'marketing' | 'preferences';

export interface CookieConsent {
  essential: true; // always true — not user-controlled
  analytics: boolean;
  marketing: boolean;
  preferences: boolean;
  // Stamp on which the user gave consent (ISO 8601). null = never decided
  // (banner shows). Renewed when user opens "Cookie preferences" + saves.
  decidedAt: string | null;
  // Bump if we change the category list — old consents become 'undecided'
  // and the banner re-appears.
  version: number;
}

const STORAGE_KEY = 'cm_cookie_consent_v1';
const CURRENT_VERSION = 1;

const DEFAULT_CONSENT: CookieConsent = {
  essential: true,
  analytics: false,
  marketing: false,
  preferences: false,
  decidedAt: null,
  version: CURRENT_VERSION,
};

interface CookieConsentContextValue {
  consent: CookieConsent;
  // True until the user has saved a choice (banner should show).
  needsDecision: boolean;
  // Force banner re-open from the footer "Cookie preferences" link.
  reopenBanner: () => void;
  bannerOpen: boolean;
  acceptAll: () => void;
  rejectAll: () => void;
  // Save a custom selection (analytics/marketing/preferences only —
  // essential stays true).
  saveCustom: (selection: Pick<CookieConsent, 'analytics' | 'marketing' | 'preferences'>) => void;
}

const CookieConsentContext = createContext<CookieConsentContextValue | undefined>(undefined);

function loadFromStorage(): CookieConsent {
  if (typeof window === 'undefined') return DEFAULT_CONSENT;
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return DEFAULT_CONSENT;
    const parsed = JSON.parse(raw) as CookieConsent;
    if (parsed.version !== CURRENT_VERSION) return DEFAULT_CONSENT;
    return { ...parsed, essential: true };
  } catch {
    return DEFAULT_CONSENT;
  }
}

function persist(consent: CookieConsent) {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(consent));
  } catch {
    // localStorage can throw in private-browsing / quota-exceeded. The
    // banner is still functional in-memory for this session.
  }
}

export function CookieConsentProvider({ children }: { children: ReactNode }) {
  const [consent, setConsent] = useState<CookieConsent>(() => loadFromStorage());
  const [bannerOpen, setBannerOpen] = useState<boolean>(() => loadFromStorage().decidedAt === null);

  // Keep consent + storage in sync if another tab updates (storage event).
  useEffect(() => {
    const onStorage = (e: StorageEvent) => {
      if (e.key === STORAGE_KEY) {
        const next = loadFromStorage();
        setConsent(next);
        setBannerOpen(next.decidedAt === null);
      }
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, []);

  const acceptAll = useCallback(() => {
    const next: CookieConsent = {
      essential: true,
      analytics: true,
      marketing: true,
      preferences: true,
      decidedAt: new Date().toISOString(),
      version: CURRENT_VERSION,
    };
    setConsent(next);
    persist(next);
    setBannerOpen(false);
  }, []);

  const rejectAll = useCallback(() => {
    const next: CookieConsent = {
      essential: true,
      analytics: false,
      marketing: false,
      preferences: false,
      decidedAt: new Date().toISOString(),
      version: CURRENT_VERSION,
    };
    setConsent(next);
    persist(next);
    setBannerOpen(false);
  }, []);

  const saveCustom = useCallback(
    (selection: Pick<CookieConsent, 'analytics' | 'marketing' | 'preferences'>) => {
      const next: CookieConsent = {
        essential: true,
        analytics: selection.analytics,
        marketing: selection.marketing,
        preferences: selection.preferences,
        decidedAt: new Date().toISOString(),
        version: CURRENT_VERSION,
      };
      setConsent(next);
      persist(next);
      setBannerOpen(false);
    },
    [],
  );

  const reopenBanner = useCallback(() => setBannerOpen(true), []);

  const needsDecision = consent.decidedAt === null;

  const value = useMemo<CookieConsentContextValue>(
    () => ({
      consent,
      needsDecision,
      reopenBanner,
      bannerOpen,
      acceptAll,
      rejectAll,
      saveCustom,
    }),
    [consent, needsDecision, reopenBanner, bannerOpen, acceptAll, rejectAll, saveCustom],
  );

  return <CookieConsentContext.Provider value={value}>{children}</CookieConsentContext.Provider>;
}

export function useCookieConsent(): CookieConsentContextValue {
  const ctx = useContext(CookieConsentContext);
  if (!ctx) {
    throw new Error('useCookieConsent must be used inside <CookieConsentProvider>');
  }
  return ctx;
}

/**
 * Convenience gate for analytics-grade calls (Sentry init, PostHog
 * track, GA pageview etc.).
 *
 * Usage:
 *   if (useAnalyticsConsent()) {
 *     Sentry.init(...)
 *   }
 *
 * Safe to call before the user has decided: returns false in that
 * window so we don't accidentally initialize non-essential trackers
 * before consent.
 */
export function useAnalyticsConsent(): boolean {
  return useCookieConsent().consent.analytics;
}
